/**
 * ChatContext — exposes ``openChat({ prefill, sourceApp })`` so any
 * route (notably ``/apps``) can pop the floating widget with a
 * pre-seeded draft. Used by the #945 "Feedback geben" buttons on
 * in-development app tiles.
 *
 * The context lives in its own module so consumers (``AppsPage``,
 * future #761 tabs) can ``useChat()`` without importing the
 * ``ChatProvider`` directly — ``ChatProvider`` owns the state and
 * provides the value.
 */
import { createContext, useContext } from 'react';

export interface ChatOpenOptions {
  /** Pre-seed the compose textarea. The literal "…" placeholder in
   * the template marks the cursor target. */
  prefill?: string;
  /** App registry id (e.g. ``saisonkraefte-verwaltung``). Recorded on
   * the message's ``source_url`` as ``app://<sourceApp>`` so the admin
   * tool can filter / group by source app. */
  sourceApp?: string;
}

export interface ChatContextValue {
  isOpen: boolean;
  open: (options?: ChatOpenOptions) => void;
  close: () => void;
  /** Consumed once by the active tab — returns the pending prefill and
   * clears it so subsequent re-renders don't keep re-injecting. */
  consumePrefill: () => ChatOpenOptions | null;
}

export const ChatContext = createContext<ChatContextValue | null>(null);

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    // Soft fallback so a Storybook / standalone render of a tab
    // doesn't crash. Acts as a no-op.
    return {
      isOpen: false,
      open: () => undefined,
      close: () => undefined,
      consumePrefill: () => null,
    };
  }
  return ctx;
}
